import React, { useState, ChangeEvent } from 'react'
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { InfinitySpin } from 'react-loader-spinner'
import { useSelector, useDispatch } from 'react-redux';
import {
  selectIsLoading, setFirstAttachment, selectLoadingMessage,
  selectWorkspaceId, selectConversationId
} from '../../store/features/genieSlice';
import { useFollowUpMutation } from '../../store/services/genieservice';
import { useClassifyMutation } from '../../store/services/dataservice';
import { selectUserId } from '../../store/features/authSlice';
import GenieLogDialog from './GenieLogDialog';
import SpeechButton from '../common/SpeechButton';
import { useStartConversationMutation } from '../../store/services/genieservice';
import { useGeniePolling } from '../../hooks/useGeniePolling';
import { setQuestion } from '../../store/features/genieSlice';

const QuestionInput = () => {
  const isLoading = useSelector(selectIsLoading)
  const [value, setValue] = useState<string>('');
  const [clearTranscriptFn, setClearTranscriptFn] = useState<(() => void) | null>(null);
  const [followUp] = useFollowUpMutation()
  const [classify] = useClassifyMutation()
  const dispatch = useDispatch()
  const loadingMessage = useSelector(selectLoadingMessage)
  const workspaceId = useSelector(selectWorkspaceId)
  const email = useSelector(selectUserId)
  const [startConversation] = useStartConversationMutation();
  const conversation_id = useSelector(selectConversationId);

  // Use custom hook for polling logic
  useGeniePolling();

  const handleInputChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    
    // If the value is cleared, also clear the transcript
    if (!newValue && clearTranscriptFn) {
      clearTranscriptFn();
    }
  }

  const handleKeyDown = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();

      if (!value) return;

      if (!workspaceId) {
        // Call classify to get workspace_id
        try {
          const classifyResponse = await classify({ query: value, email }).unwrap();
          
          // Check if classifyResponse exists and has a valid workspace_id
          if (classifyResponse && classifyResponse.workspace_id && 
              classifyResponse.workspace_id.trim() !== '') {
            // Workspace ID is now set in the store, proceed with follow up
            await startConversationOperation();
          } else {
            // Clear the input since we can't proceed
            setValue('');
            if (clearTranscriptFn) {
              clearTranscriptFn();
            }
          }
        } catch (error: any) {
          console.error("Error in classify:", error);
          // Clear the input since we can't proceed
          setValue('');
          if (clearTranscriptFn) {
            clearTranscriptFn();
          }
        }
        return;
      } else {
        await handleFollowUp();
      }
    }
  }

  const startConversationOperation = async () => {
    try {
      dispatch(setQuestion(value));
      
      await startConversation({ 
        content: value
      }).unwrap();
      
      // Clear the input and transcript after sending
      setValue('');
      if (clearTranscriptFn) {
        clearTranscriptFn();
      }
    } catch (error: any) {
      console.error("Error in startConversation:", error);
    }
  };

  const handleFollowUp = async () => {
    dispatch(setFirstAttachment(null));

    if (value && conversation_id) {
      try {
        dispatch(setQuestion(value));
        // Call follow up question with the input value and conversation_id
        await followUp({
          content: value,
          conversation_id
        }).unwrap();
        
        // Clear the input and transcript after sending
        setValue('');
        if (clearTranscriptFn) {
          clearTranscriptFn();
        }
      } catch (error: any) {
        console.error("Error in followUp:", error);
      }
    }
  }

  const handleQuestionSelect = (selectedQuestion: string) => {
    setValue(selectedQuestion);
  };

  const handleSpeechResult = (text: string) => {
    setValue(text);
  };

  const handleSpeechPartialResult = (text: string) => {
    setValue(text);
  };

  // Add a function to clear the input when starting speech
  const handleStartListening = () => {
    setValue(''); // Clear the input field
  };

  // Handle setting the clearTranscript function from SpeechButton
  const handleClearTranscript = (clearTranscript: () => void) => {
    setClearTranscriptFn(() => clearTranscript);
  };

  if (isLoading)
    return (<Grid item xs={12} sx={{ textAlign: 'center', color: '#000' }}>
      {loadingMessage}... <br />
      <InfinitySpin
        width="200"
        color="#4fa94d"
      /> <br />
      {value}
    </Grid>)

  if (!isLoading)
    return (
      (<>
        <Grid container sx={{ px: 1 }}>
          <Grid item xs={1} sx={{ px: 0 }}>
            <GenieLogDialog onQuestionSelect={handleQuestionSelect} />
          </Grid>
          <Grid item xs={11} sx={{ px: 0 }}>
            <TextField
              fullWidth
              label="Genie - Ask something about your data" variant="outlined"
              value={value} onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SpeechButton
                      onResult={handleSpeechResult}
                      onPartialResult={handleSpeechPartialResult}
                      onStartListening={handleStartListening}
                      onClearTranscript={handleClearTranscript}
                      size="small"
                    />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
      </>
      ))

  return <></>
}

export default QuestionInput